import React, { FC, useCallback, useEffect, useMemo, useState } from "react";
import Modal from "@/components/Modal/index";
import { useAppTheme } from "@/utils/theme";
import Box from "@mui/material/Box";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import inviteValidationSchema, { InviteFormData } from "./validation.schema";
import { useTranslation } from "react-i18next";
import Button from "@/components/Button";
import { useFeedBack } from "@/providers/FeedBackProvider/FeedBackContext";
import { ErrorResponse } from "@/domain/error.interface";
import { ACTION_CODE, MODULE_NAME, OPTIONS } from "@/domain/auditlog.enum";
import { useUserStore } from "@/hooks/useUserStore";
import { useCreateAuditlog } from "@/hooks/useAuditLogs";
import {
  useColoringRequiredSetting,
  useColorModuleSetting,
  useCountrySetting,
  useDateOfBirthSetting,
  useParticipantReleaseDate,
  usePhoneNumberSetting,
  useStateIdSetting,
} from "@/hooks/useSetting";
import { useGetUserPrograms } from "@/hooks/usePrograms";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import PhoneNumberInput from "@/components/PhoneNumberInput";
import { Typography } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import Datepicker from "@/components/Datepicker";
import dayjs, { Dayjs } from "dayjs";
import Select from "@/components/Select";
import TreatmentServiceAssignment from "./TreatmentServiceAssignment";
import FirstAppointment from "./FirstAppointment";
import ValidationBanner from "./ValidationBanner";
import { readAppointmentValidationError } from "@/utils/appointmentValidationError";
import { useOrganizationUnitsByIds } from "@/hooks/useOrganizations";
import { useCreateClient } from "@/hooks/useClients";
import { useCategoryByType } from "@/hooks/useCategories";
import { CategoryElement } from "@/domain/category.interface";

type ClientInviteModalProps = {
  open: boolean;
  onClose: () => void;
  onInvited?: (data: {
    participantId: string;
    enrollmentId?: string;
    programId: string;
  }) => void;
};

const ClientInviteModal: FC<ClientInviteModalProps> = ({
  open,
  onClose,
  onInvited,
}) => {
  const { t } = useTranslation("common");
  const { userInfo, hasSystemAdminRole } = useUserStore();
  const { showSnackBar, showSimpleAlert } = useFeedBack();
  const theme = useAppTheme();

  const createAuditLog = useCreateAuditlog();
  const createClient = useCreateClient();
  const isPending = createClient.isPending;

  const { programs, isLoadingPrograms } = useGetUserPrograms({
    userId: userInfo?._id as string,
    hasSystemAdminRole,
  });

  const { category, isLoadingCategory } = useCategoryByType({
    type: "COLORS",
    status: "ACTIVE",
  });

  const colors = useMemo(() => {
    if (!category) return [];

    return category?.categories.map((categoryElement: CategoryElement) => {
      const name = categoryElement.languages.find(
        (language) => language.language?.toLowerCase() === "en-us"
      )?.name as string;

      return {
        key: categoryElement._id,
        value: name,
        hexaColor: categoryElement.hexaColor,
        extra: (
          <div
            className="rounded-full w-6 h-6"
            style={{
              backgroundColor: categoryElement.hexaColor,
            }}
          ></div>
        ),
      };
    });
  }, [category]);

  const programsOptions = useMemo(() => {
    if (!programs) return [];

    return programs.map((program) => ({
      key: program._id,
      value: program.name,
    }));
  }, [programs]);

  const [selectedOrgUnitIds, setSelectedOrgUnitIds] = useState<string[]>([]);

  const {
    organizationUnits,
    isLoadingOrganizationUnits,
    refetchOrganizationUnits,
  } = useOrganizationUnitsByIds({
    ids: selectedOrgUnitIds,
    status: "ACTIVE",
  });

  const orgUnitsOptions = useMemo(() => {
    if (!organizationUnits) return [];

    if (hasSystemAdminRole) {
      return organizationUnits.map((orgUnit) => ({
        key: orgUnit._id,
        value: orgUnit.name,
      }));
    }

    if (!userInfo?.programs) return [];

    const userAccessibleOrgUnitIds = userInfo.programs.flatMap(
      (program) => program.orgUnitIds
    );

    const filteredOrgUnits = organizationUnits.filter((orgUnit) =>
      userAccessibleOrgUnitIds.includes(orgUnit._id)
    );

    return filteredOrgUnits.map((orgUnit) => ({
      key: orgUnit._id,
      value: orgUnit.name,
    }));
  }, [organizationUnits, hasSystemAdminRole, userInfo?.programs]);

  const countrySetting = useCountrySetting();
  const dateOfBirthSetting = useDateOfBirthSetting();
  const stateIdSetting = useStateIdSetting();
  const phoneNumberSetting = usePhoneNumberSetting();

  const colorModuleSetting = useColorModuleSetting();
  const colorRequired = useColoringRequiredSetting();
  const participantReleaseDateSetting = useParticipantReleaseDate();

  const country = useMemo(() => countrySetting, [countrySetting]);
  const dateOfBirth = useMemo(() => dateOfBirthSetting, [dateOfBirthSetting]);
  const stateIdRequired = useMemo(() => stateIdSetting, [stateIdSetting]);
  const phoneNumberRequired = useMemo(
    () => phoneNumberSetting,
    [phoneNumberSetting]
  );
  const isParticipantReleaseDateMandatory = useMemo(
    () => participantReleaseDateSetting === "mandatory",
    [participantReleaseDateSetting]
  );

  const coloringModule = useMemo(
    () => colorModuleSetting,
    [colorModuleSetting]
  );
  const coloringRequired = useMemo(() => colorRequired, [colorRequired]);

  const {
    handleSubmit,
    control,
    setValue,
    watch,
    formState: { errors },
  } = useForm<InviteFormData>({
    resolver: yupResolver(
      inviteValidationSchema(
        t,
        country,
        dateOfBirth,
        stateIdRequired,
        phoneNumberRequired,
        isParticipantReleaseDateMandatory,
        coloringModule,
        coloringRequired
      )
    ),
    mode: "all",
  });

  const programId = watch("programId") as string;
  const preferredName = watch("preferredName") as string;
  // PRET-1837 -- drives the filtered location list and the read-only echo.
  const treatmentAgencyId = watch("treatmentAgencyId") as string;
  const locationId = watch("locationId") as string;
  const firstAppointmentDate = watch("firstAppointmentDate") as string;
  const firstAppointmentStart = watch("firstAppointmentStart") as string;
  const firstAppointmentEnd = watch("firstAppointmentEnd") as string;
  const firstAppointmentReasonId = watch("firstAppointmentReasonId") as string;
  const [allocationNames, setAllocationNames] = useState<{
    agencyName?: string;
    locationName?: string;
  }>({});

  // Design screen 5a annotation 4: INVITE stays disabled until BOTH sections are complete, and the
  // blocking condition is written out rather than left implied.
  const allocationComplete = Boolean(treatmentAgencyId && locationId);
  const firstAppointmentComplete = Boolean(
    firstAppointmentDate &&
    firstAppointmentStart &&
    firstAppointmentEnd &&
    firstAppointmentReasonId
  );
  const enrolmentIncomplete = !allocationComplete || !firstAppointmentComplete;

  /**
   * The blocking reason names the missing half AND states the overall gate, per frame 8a
   * (element 8a/gate in the Hi-Fi handoff v0.5):
   *
   *   "Select a location to complete the treatment service assignment. Both sections must be
   *    complete before this participant can be invited."
   *
   * The first sentence was already correct; the second was missing, so a coordinator who fixed
   * the named half had no indication that the other section still gated INVITE.
   */
  const BOTH_SECTIONS =
    "Both sections must be complete before this participant can be invited.";

  const blockingReason = !treatmentAgencyId
    ? `Select a treatment agency to complete the treatment service assignment. ${BOTH_SECTIONS}`
    : !locationId
      ? `Select a location to complete the treatment service assignment. ${BOTH_SECTIONS}`
      : !firstAppointmentComplete
        ? `Record the first appointment to complete enrolment. ${BOTH_SECTIONS}`
        : null;

  /**
   * Expected validation rejections are shown inline, beside the fields that caused them. The alert
   * modal stays for the unexpected — an email that already exists, a request that fell over.
   */
  const [validationMessage, setValidationMessage] = useState<string | null>(
    null
  );

  /**
   * Routes one rejection: inline for our own validation outcomes, alert for everything else.
   * Returns true when it handled it inline.
   *
   * Shared by the mutation's onError AND the enclosing catch, because `mutateAsync` rejects even
   * after onError has run — handling it in only one place still let the alert through.
   */
  const showValidationInline = useCallback((error: unknown): boolean => {
    const message = readAppointmentValidationError(
      error,
      "The first appointment date is not valid for this participant."
    );
    if (message === null) return false;
    setValidationMessage(message);
    return true;
  }, []);

  // A new attempt should not sit under the previous complaint.
  useEffect(() => {
    setValidationMessage(null);
  }, [
    firstAppointmentDate,
    firstAppointmentStart,
    firstAppointmentEnd,
    treatmentAgencyId,
    locationId,
  ]);

  const getRandomColorId = useCallback(() => {
    if (!colors) return "";

    const randomIndex = Math.floor(Math.random() * colors.length);

    return colors[randomIndex].key;
  }, [colors]);

  const handleFirstNameBlur = useCallback(
    (firstName: string) => {
      if (firstName && !preferredName) {
        setValue("preferredName", firstName);
      }
    },
    [preferredName, setValue]
  );

  const formatDateToISO = (date: string | number | Date) => {
    const d = new Date(date);
    return (
      d.getFullYear() +
      "-" +
      String(d.getMonth() + 1).padStart(2, "0") +
      "-" +
      String(d.getDate()).padStart(2, "0")
    );
  };

  const onSubmit = async (data: InviteFormData) => {
    const colorId =
      data.colorId === "random" ? getRandomColorId() : data.colorId;

    const payload = {
      firstName: data.firstName as string,
      lastName: data.lastName as string,
      preferredName: data.preferredName as string,
      email: data.email as string,
      countryPhoneCode: country === "US" ? "1" : "44",
      phoneNumber: data.phoneNumber as string,
      externalId: data.externalId as string,
      createdBy: userInfo?._id as string,
      dateOfBirth: data.dateOfBirth
        ? formatDateToISO(data.dateOfBirth as string | number | Date)
        : undefined,
      programId: data.programId as string,
      orgUnitId: data.orgUnitIds as string,
      caseNumbers: data.caseNumbers as string,
      status: "INVITED",
      releaseDate: data.releaseDate
        ? formatDateToISO(data.releaseDate as string | number | Date)
        : undefined,
      // PRET-1837 AC1 -- the allocation is saved against the enrolment.
      treatmentAgencyId: data.treatmentAgencyId as string,
      locationId: data.locationId as string,
      // The pickers hand back Dayjs objects; the API wants a calendar date and wall-clock times,
      // which it joins as `${date}T${start}`.
      firstAppointmentDate: dayjs(data.firstAppointmentDate as Dayjs).format(
        "YYYY-MM-DD"
      ),
      firstAppointmentStart: dayjs(data.firstAppointmentStart as Dayjs).format(
        "HH:mm:ss"
      ),
      firstAppointmentEnd: dayjs(data.firstAppointmentEnd as Dayjs).format(
        "HH:mm:ss"
      ),
      firstAppointmentReasonId: data.firstAppointmentReasonId as string,
      ...(coloringModule && colorId ? { colorId: colorId } : {}),
    };
    try {
      await createClient.mutateAsync(payload, {
        onSuccess: (data) => {
          onInvited?.({
            participantId: data._id,
            enrollmentId: data.enrollmentId,
            programId: payload.programId,
          });
          onClose();
          showSnackBar("The client has been invited.", "success");
          createAuditLog.mutate({
            appType: "WEB_BACK_OFFICE",
            module: MODULE_NAME.PARTICIPANTS_LIST,
            option: OPTIONS.PARTICIPANT_ADD,
            actionCode: ACTION_CODE.WEB_PART_ADD_INVITE,
            action: "When inviting a new client (adding)",
            detail: `Invited a new client ${data.firstName} ${data.lastName}`,
            transactionDate: new Date(),
            accountId: `${process.env.REACT_APP_ACCOUNT_ID}`,
            createdBy: userInfo?._id as string,
            programId: payload.programId,
            orgUnitId: payload.orgUnitId,
            targetId: data._id,
          });
        },
        onError: (error: ErrorResponse) => {
          if (showValidationInline(error)) return;
          showSimpleAlert(
            (Array.isArray(error.message) ? error.message[0] : error.message) ||
              error.error ||
              "An error occurred"
          );
        },
      });
    } catch (error: unknown) {
      if (showValidationInline(error)) return;
      const err = error as ErrorResponse;
      showSimpleAlert(
        (Array.isArray(err.message) ? err.message[0] : err.message) ||
          err.error ||
          "An error occurred"
      );
    }
  };

  useEffect(() => {
    if (selectedOrgUnitIds.length > 0) {
      refetchOrganizationUnits();
    }
  }, [selectedOrgUnitIds]);

  useEffect(() => {
    if (programs && programs.length > 0) {
      const selectedProgram = programs.find(
        (program) => program._id === programId
      );

      if (selectedProgram) {
        let orgUnitIds =
          selectedProgram.orgUnitIds || selectedProgram.orgUnitId || [];

        if (!hasSystemAdminRole && userInfo?.programs) {
          // Get the user's accessible org unit IDs for this specific program
          const userProgramAccess = userInfo.programs.find(
            (userProgram) => userProgram.programId === programId
          );

          if (userProgramAccess) {
            // Only show org units that are both in the program AND the user has access to
            orgUnitIds = orgUnitIds.filter((orgUnitId) =>
              userProgramAccess.orgUnitIds.includes(orgUnitId)
            );
          } else {
            // User doesn't have access to this program, show no org units
            orgUnitIds = [];
          }
        }
        setSelectedOrgUnitIds(orgUnitIds);
      } else {
        setSelectedOrgUnitIds([]);
      }
    }
  }, [programs, programId, hasSystemAdminRole, userInfo?.programs]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      isPending={false}
      title="Invite client"
      theme={theme}
      size={{
        width: "100%",
        minHeight: 460,
        maxHeight: 720,
        maxWidth: 860,
      }}
      content={
        <Box sx={{ width: "100%", minHeight: 380, padding: "20px 0" }}>
          <Grid container spacing={"20px"}>
            <Grid item xs={12} sm={12}>
              <Typography
                variant="titleSmall"
                fontWeight={700}
                gutterBottom
                color={theme.palette.menu.dark}
              >
                Personal information
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name="firstName"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <TextField
                    {...field}
                    id="firstName"
                    label="First name"
                    variant="outlined"
                    required
                    value={field.value}
                    onChange={(value) => field.onChange(value)}
                    onBlur={() => handleFirstNameBlur(field.value as string)}
                    error={!!errors.firstName}
                    helperText={
                      errors.firstName ? errors.firstName.message : null
                    }
                    fullWidth
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name="lastName"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <TextField
                    {...field}
                    id="lastName"
                    label="Last name"
                    variant="outlined"
                    required
                    value={field.value}
                    onChange={(value) => field.onChange(value)}
                    error={!!errors.lastName}
                    helperText={
                      errors.lastName ? errors.lastName.message : null
                    }
                    fullWidth
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name="preferredName"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <TextField
                    {...field}
                    id="preferredName"
                    label="Preferred name"
                    variant="outlined"
                    required
                    value={field.value}
                    onChange={(value) => field.onChange(value)}
                    error={!!errors.preferredName}
                    helperText={
                      errors.preferredName ? errors.preferredName.message : null
                    }
                    fullWidth
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name="email"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <TextField
                    {...field}
                    id="email"
                    label="Email"
                    required
                    variant="outlined"
                    value={field.value}
                    onChange={(value) => field.onChange(value)}
                    error={!!errors.email}
                    helperText={errors.email ? errors.email.message : null}
                    fullWidth
                  />
                )}
              />
            </Grid>
            {dateOfBirth !== "hidden" ? (
              <Grid item xs={12} sm={6}>
                <Controller
                  name="dateOfBirth"
                  control={control}
                  render={({ field }) => (
                    <div>
                      <Datepicker
                        {...field}
                        label="Date of birth (dd/mm/yyyy)"
                        onChange={(value) => {
                          field.onChange(
                            value !== undefined ? value?.toDate() : undefined
                          );
                        }}
                        value={
                          field.value
                            ? dayjs(
                                field.value as string | number | Date | Dayjs
                              )
                            : null
                        }
                        error={!!errors.dateOfBirth}
                        helperText={errors.dateOfBirth?.message || ""}
                        required={dateOfBirth === "mandatory"}
                      />
                    </div>
                  )}
                />
              </Grid>
            ) : null}
            <Grid item xs={12} sm={6}>
              <Controller
                name="phoneNumber"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <PhoneNumberInput
                    {...field}
                    defaultCode={country}
                    value={field.value}
                    label="Phone number"
                    variant="outlined"
                    required={phoneNumberRequired === "mandatory"}
                    className="flex flex-1 w-full"
                    onChange={(value) => field.onChange(value)}
                    error={!!errors.phoneNumber}
                    helperText={
                      errors.phoneNumber ? errors.phoneNumber.message : null
                    }
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={12}>
              <Typography
                variant="titleSmall"
                fontWeight={700}
                gutterBottom
                color={theme.palette.menu.dark}
              >
                PRET information
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name="programId"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <Select
                    handleChange={(value) => {
                      field.onChange(value);
                      // Clear org unit selection when program changes
                      setValue("orgUnitIds", "");
                    }}
                    data={programsOptions || []}
                    value={field.value as string}
                    label="Program"
                    required
                    id="client-program"
                    isLoading={isLoadingPrograms}
                    fullWidth
                    error={!!errors.programId}
                    errorMessage={errors.programId?.message}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name="orgUnitIds"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <Select
                    handleChange={(value) => {
                      field.onChange(value);
                    }}
                    data={orgUnitsOptions || []}
                    value={field.value as string}
                    label="Org unit"
                    required
                    id="client-orgUnitIds"
                    isLoading={!programId || isLoadingOrganizationUnits}
                    fullWidth
                    error={!!errors.orgUnitIds}
                    errorMessage={errors.orgUnitIds?.message}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name="caseNumbers"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    id="caseNumbers"
                    label="State ID"
                    variant="outlined"
                    className="flex flex-1"
                    required={stateIdRequired === "mandatory"}
                    value={field.value}
                    onChange={(value) => field.onChange(value)}
                    error={!!errors.caseNumbers}
                    helperText={
                      errors.caseNumbers ? errors.caseNumbers.message : null
                    }
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name="externalId"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    id="externalId"
                    label="External ID"
                    variant="outlined"
                    className="flex flex-1"
                    value={field.value}
                    onChange={(value) => field.onChange(value)}
                    error={!!errors.externalId}
                    helperText={
                      errors.externalId ? errors.externalId.message : null
                    }
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name="releaseDate"
                control={control}
                render={({ field }) => (
                  <div>
                    <Datepicker
                      {...field}
                      label="Release date"
                      onChange={(value) => {
                        field.onChange(
                          value !== undefined ? value?.toDate() : undefined
                        );
                      }}
                      value={
                        field.value
                          ? dayjs(field.value as string | number | Date | Dayjs)
                          : null
                      }
                      error={!!errors.releaseDate}
                      helperText={errors.releaseDate?.message || ""}
                      required={isParticipantReleaseDateMandatory}
                      disableFuture={false}
                      // Same rule as the edit modal: a release date cannot be in the past. Fixed
                      // in both, because the picker here had the identical gap and an enrolment
                      // created with a past release date is what makes the first-appointment
                      // rules meaningless downstream.
                      minDate={dayjs().startOf("day")}
                    />
                  </div>
                )}
              />
            </Grid>
            <TreatmentServiceAssignment
              control={control}
              errors={errors}
              setValue={setValue}
              accountId={userInfo?.accountId}
              userId={userInfo?._id}
              open={open}
              selectedAgencyId={treatmentAgencyId}
              selectedLocationId={locationId}
              onNamesResolved={setAllocationNames}
            />
            <FirstAppointment
              control={control}
              errors={errors}
              setValue={setValue}
              accountId={userInfo?.accountId}
              userId={userInfo?._id}
              open={open}
              agencyName={allocationNames.agencyName}
              locationName={allocationNames.locationName}
            />
            {validationMessage ? (
              <Grid item xs={12}>
                <ValidationBanner title={validationMessage} />
              </Grid>
            ) : null}
            {enrolmentIncomplete ? (
              <Grid item xs={12}>
                <Box
                  sx={{
                    display: "flex",
                    gap: 1,
                    alignItems: "center",
                    borderRadius: 1,
                    p: 1.5,
                    // Warning treatment from the handoff's token table: warning-ink on
                    // warning-light. It was a dashed grey box, which reads as a hint rather than
                    // the reason INVITE is disabled. These exact values are used because the live
                    // build's orange-on-orange fails 4.5:1 — see "Accessibility corrections".
                    backgroundColor: "#FFE7C2",
                    color: "#7A3200",
                  }}
                >
                  <InfoOutlinedIcon fontSize="small" />
                  <Typography variant="body2">{blockingReason}</Typography>
                </Box>
              </Grid>
            ) : null}
            {coloringModule ? (
              <Grid item xs={12} sm={6}>
                <Controller
                  name="colorId"
                  control={control}
                  defaultValue={
                    coloringModule && coloringRequired ? "random" : ""
                  }
                  render={({ field }) => (
                    <Select
                      handleChange={(value) => field.onChange(value)}
                      data={[{ key: "random", value: "(Random)" }, ...colors]}
                      value={field.value as string}
                      label="Screening Color"
                      id="participant-screening-color"
                      isLoading={isLoadingCategory}
                      required={coloringRequired}
                      showClearOption={!coloringRequired}
                      renderValue={(selected) => {
                        if (selected === "random") return "(Random)";

                        return (
                          <div className="flex gap-2.5">
                            <div
                              className="rounded-full w-6 h-6"
                              style={{
                                backgroundColor: colors.find(
                                  (color) => color.key === selected
                                )?.hexaColor,
                              }}
                            ></div>
                            {
                              colors.find((color) => color.key === selected)
                                ?.value
                            }
                          </div>
                        );
                      }}
                      error={!!errors.colorId}
                      errorMessage={errors.colorId?.message}
                    />
                  )}
                />
              </Grid>
            ) : null}
          </Grid>
        </Box>
      }
      actions={
        <Box
          sx={{
            display: "flex",
            gap: "10px",
          }}
        >
          <Button
            variantType="cancel"
            onClick={onClose}
            disabled={isPending}
            label="CANCEL"
          />
          <Button
            variantType="save"
            onClick={async () => {
              handleSubmit(onSubmit)();
            }}
            disabled={isPending || enrolmentIncomplete}
            isLoading={isPending}
            label="INVITE"
          />
        </Box>
      }
    />
  );
};

export default ClientInviteModal;
